import { create } from 'zustand';
import { audioService } from '../services/AudioService';

interface AudioState {
  masterVolume: number;
  musicVolume: number;
  sfxVolume: number;
}

interface AudioActions {
  setMasterVolume: (v: number) => void;
  setMusicVolume: (v: number) => void;
  setSfxVolume: (v: number) => void;
}

const STORAGE_KEY = 'usagi_audio_settings';

const DEFAULTS: AudioState = {
  masterVolume: 1,
  musicVolume: 0.7,
  sfxVolume: 0.8,
};

function loadFromStorage(): AudioState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return DEFAULTS;
    }
    const parsed = JSON.parse(raw) as Partial<AudioState>;
    return {
      masterVolume: parsed.masterVolume ?? DEFAULTS.masterVolume,
      musicVolume: parsed.musicVolume ?? DEFAULTS.musicVolume,
      sfxVolume: parsed.sfxVolume ?? DEFAULTS.sfxVolume,
    };
  } catch {
    return DEFAULTS;
  }
}

function saveToStorage(state: AudioState): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // ignore (quota / private mode)
  }
}

const initial = loadFromStorage();

// Sync with the service immediately so persisted values apply before the first
// play (the service creates its context lazily but already retains the volumes
// to apply them as soon as the graph is initialized).
audioService.setMasterVolume(initial.masterVolume);
audioService.setMusicCategoryVolume(initial.musicVolume);
audioService.setSfxCategoryVolume(initial.sfxVolume);

/**
 * Persists to `localStorage` (key `usagi_audio_settings`) independently of run
 * saves, so settings hold between sessions and even after a save wipe.
 */
export const useAudioStore = create<AudioState & AudioActions>((set, get) => ({
  ...initial,

  setMasterVolume: (v) => {
    set({ masterVolume: v });
    audioService.setMasterVolume(v);
    saveToStorage(get());
  },
  setMusicVolume: (v) => {
    set({ musicVolume: v });
    audioService.setMusicCategoryVolume(v);
    saveToStorage(get());
  },
  setSfxVolume: (v) => {
    set({ sfxVolume: v });
    audioService.setSfxCategoryVolume(v);
    saveToStorage(get());
  },
}));
